import { useState, type CSSProperties } from 'react'
import clsx from 'clsx'

import { data } from '../../lib/data'
import { saveTextFile, transcriptFilename, transcriptMarkdown } from '../../lib/transcript-file'
import { Download } from './icons'

// ============================================================================
// DOWNLOAD TRANSCRIPT — one button, two shapes, for a transcribed lecture (owner, 2026-09-17:
// "a download icon next to Practice … it'll just download it to my Downloads", and the same on
// the Recent recordings list).
//
// FETCHED ON THE PRESS, NOT BEFORE. A course page lists a semester of lectures; pulling every
// transcript so a button might be pressed would be a page that reads a hundred thousand words
// to draw an icon. The press reads exactly one lecture, formats it (lib/transcript-file.ts), and
// hands the browser the file.
//
// THE THREE STATES ARE SAID, NOT IMPLIED. Busy disables the button and says "Fetching…"; a
// failure keeps the button, turns it the danger colour and puts the reason in its label and its
// tooltip — so a press that produced no file never looks like a press that did.
// ============================================================================

type Phase = { kind: 'idle' } | { kind: 'busy' } | { kind: 'failed'; reason: string }

export function DownloadTranscriptButton({
  lectureId,
  variant,
  className,
}: {
  lectureId: string
  /** `pill` sits in the lecture row's action strip beside Practice; `icon` sits at a row's edge. */
  variant: 'pill' | 'icon'
  className?: string
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  async function download() {
    if (phase.kind === 'busy') return
    setPhase({ kind: 'busy' })
    try {
      const detail = await data.getLectureTranscript(lectureId)
      if (!detail) {
        setPhase({ kind: 'failed', reason: 'There is no transcript for this lecture yet.' })
        return
      }
      saveTextFile(transcriptFilename(detail), transcriptMarkdown(detail))
      setPhase({ kind: 'idle' })
    } catch (error) {
      setPhase({
        kind: 'failed',
        reason: error instanceof Error ? error.message : 'The transcript could not be fetched.',
      })
    }
  }

  const label =
    phase.kind === 'busy'
      ? 'Fetching the transcript…'
      : phase.kind === 'failed'
        ? `Download failed — ${phase.reason}`
        : 'Download transcript (.md)'

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={(e) => {
          // The row around this is a link; the press is for the file, not the page.
          e.preventDefault()
          e.stopPropagation()
          void download()
        }}
        disabled={phase.kind === 'busy'}
        aria-label={label}
        title={label}
        data-keeps-click
        className={clsx(
          'flex size-8 flex-none items-center justify-center rounded-button transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)]',
          phase.kind === 'failed' ? 'text-danger-2' : 'text-muted hover:bg-fill-2 hover:text-ink',
          'disabled:opacity-50',
          className,
        )}
      >
        <Download size={15} />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={phase.kind === 'busy'}
      aria-label={label}
      title={label}
      style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
      className={clsx(
        'mo-ink mo-press flex items-center gap-[7px] rounded-button border bg-surface px-3 py-[7px] text-[11.5px] font-semibold leading-none',
        phase.kind === 'failed' ? 'border-danger-line text-danger-2' : 'border-line-2 text-ink-2',
        'disabled:opacity-60',
        className,
      )}
    >
      <Download size={13} className={phase.kind === 'failed' ? 'text-danger-2' : 'text-school'} />
      {phase.kind === 'busy' ? 'Fetching…' : phase.kind === 'failed' ? 'Try again' : 'Download'}
    </button>
  )
}
